import { PRIMARY_BUTTON } from "@/constants";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import PaginationComponent from "@/app/components/ui/Pagination";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useWareHouseListAPI } from "@/app/hooks/api_hooks/configuration/WareHouse/useWareHouseListAPI";
import { useWareHouseSearchAPI } from "@/app/hooks/api_hooks/configuration/WareHouse/useWareHouseSearchAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useEffect, useMemo, useState } from "react";
import { FaCity } from "react-icons/fa6";
import { useNavigate } from "react-router";
import clsx from "clsx";

const PageSize = 10;

const tableHeader = [
  { item: "SN" },
  { item: "Warehouse Name" },
  { item: "Clinic Name" },
  { item: "Status" },
  { item: "Action" },
];

const WarehouseListComponent = () => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("CONFIGURATION", "id");
  const { routeState } = useRouteInfo(routeId);
  const { wareHouseList } = routeState.state as IConfigurationListState;
  const { execute: fetchWareHouse } = useWareHouseListAPI();
  const { execute: searchWarehouse } = useWareHouseSearchAPI();

  const [currentPage, setCurrentPage] = useState(1);
  const [mydata, setMydata] = useState([]);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return mydata?.slice(firstPageIndex, lastPageIndex);
  }, [mydata, currentPage, PageSize]);

  useEffect(() => {
    setCurrentPage(1);
    setMydata(wareHouseList);
  }, [wareHouseList]);

  const { NO_PWL_RESULTS } = useImageConstants();

  const navigate = useNavigate();

  const isWarehouseListLoading = useLoader("isOnboardWareHouseListInProgress");

  useEffect(() => {
    fetchWareHouse();
  }, []);

  const handelEditNavigate = (id: string) => {
    navigate(`/app/configuration/warehouse-onboard?warehouse_id=${id}`);
  };

  return (
    <>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-4 rounded-md">
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-semibold text-sm sm:text-base text-gray-900 leading-6 whitespace-nowrap">Warehouse</h1>
            <p className="mt-2 text-gray-700 text-xs sm:text-sm hidden sm:block">
              A list of all the Warehouse onboarded in the VMS.
            </p>
          </div>
          <div className="shrink-0">
            <button
              type="button"
              onClick={() => navigate(getRouteKey("WAREHOUSE_ONBOARD", "url"))}
              className={clsx(PRIMARY_BUTTON, "whitespace-nowrap")}
            >
              Onboard New Warehouse
            </button>
          </div>
        </div>
        <div className="mt-3">
          <InputWithAddOn
            onEnterKeyPress={(query: string) => {
              if (query.length === 0) {
                fetchWareHouse();
              } else {
                searchWarehouse(query);
              }
            }}
            containerClassName="w-full sm:w-1/2 lg:w-1/3"
            addon={<FaCity className="w-4 h-4" />}
            placeholder="Search By Clinic"
          />
        </div>
        {isWarehouseListLoading ? (
          <div className="mt-4 ">
            <SkeletonType1 className="mt-8" />
          </div>
        ) : wareHouseList?.length > 0 ? (
          <div className="mt-8 flow-root">
            <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[600px]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    <div className="bg-white rounded-lg">
                      <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr>
                            {tableHeader?.map((key, index) => (
                              <th
                                key={index}
                                scope="col"
                                className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                              >
                                {key?.item}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="overflow-hidden rounded-xl w-full">
                          {currentTableData?.map((item, idx) => {
                            const isFirst = idx === 0;
                            const isLast = idx === currentTableData?.length - 1;
                            const isSingle = currentTableData?.length === 1;
                            const isActive = String(item?.status).toLowerCase() === "active";

                            return (
                              <tr
                                key={item?.warehouse_id ?? idx}
                                className={clsx(
                                  "group bg-white transition",
                                  isSingle && "rounded-xl",
                                  isFirst && !isSingle && "rounded-t-xl",
                                  isLast && !isSingle && "rounded-b-xl",
                                )}
                              >
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                    isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                    isFirst && !isSingle && "rounded-tl-2xl",
                                    isLast && !isSingle && "rounded-bl-2xl",
                                  )}
                                >
                                  {(currentPage - 1) * PageSize + (idx + 1)}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                  {item?.warehouse_display_name}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                  {item?.clinic_name}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                  <div
                                    className={clsx(
                                      "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium",
                                      isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700",
                                    )}
                                  >
                                    <span
                                      className={clsx("w-2 h-2 rounded-full", isActive ? "bg-green-500" : "bg-red-500")}
                                    />
                                    {isActive ? "Active" : "InActive"}
                                  </div>
                                </td>
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                    isSingle && "rounded-tr-2xl rounded-br-2xl",
                                    isFirst && !isSingle && "rounded-tr-2xl",
                                    isLast && !isSingle && "rounded-br-2xl",
                                  )}
                                >
                                  <a
                                    onClick={(event) => {
                                      event.preventDefault();
                                      handelEditNavigate(item?.warehouse_id);
                                    }}
                                    href="#"
                                    className="underline text-xs text-black"
                                  >
                                    Edit<span className="sr-only">, {item?.warehouse_display_name}</span>
                                  </a>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center items-center p-4 w-full overflow-x-auto">
              <PaginationComponent
                className="pagination-bar"
                currentPage={currentPage}
                totalCount={wareHouseList?.length}
                pageSize={PageSize}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>
        ) : (
          <NoDataFoundComponent
            imageURL={NO_PWL_RESULTS}
            message="No Warehouse have been onboarded"
            description="No city data is available try onboarding a Warehouse"
          />
        )}
      </div>
    </>
  );
};

export default WarehouseListComponent;
